import { type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import qs from 'qs';

import { makePickSavingField } from '@helpers/makePickSavingField';

import type { ITableSorting } from '@model/tableSorting';

import { apiClient } from '@services/apiClient';

import { type ICostSavingDetailsKeys, type ICostSavingsDetailsYearStats } from '../model/costSavingDetails';

const totalCostSavingsKey = (
  keys: ICostSavingDetailsKeys,
): [string, Array<number>, ITableSorting, boolean, string] => [
  'costSavingsDetails',
  keys.locationIds,
  keys.sorting,
  keys.includeUnverifiedSavings,
  keys.year,
];

const totalCostSavingsFn = ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof totalCostSavingsKey>, number>) => {
  const orderBy = queryKey[2].id ? queryKey[2].id : undefined;
  const direction = !queryKey[2].id ? undefined : queryKey[2].desc ? 'desc' : 'asc';

  const params = {
    location_ids: queryKey[1],
    sort: {
      field: orderBy,
      direction: direction,
    },
    include_unverified_savings: +queryKey[3],
    year: queryKey[4],
  };

  return apiClient.get<{ data: ICostSavingsDetailsYearStats }>(
    `${APP_API_URL_V2}fsd/action-item/cost-savings/by-year`,
    {
      params,
      paramsSerializer: {
        serialize: (params) => qs.stringify(params),
      },
    },
  );
};

const totalCostSavingsSelect =
  (locationIds: Array<number>) =>
  ({ data }: AxiosResponse<{ data: ICostSavingsDetailsYearStats }>) => {
    const pick = makePickSavingField(!locationIds);

    const {
      costAvoidedCustomerCurrency,
      costAvoidedFacilityCurrency,
      equipmentLaborSavingCustomerCurrency,
      equipmentLaborSavingFacilityCurrency,
      ...rest
    } = data?.data;

    return {
      ...rest,
      equipmentLaborSaving: pick(equipmentLaborSavingCustomerCurrency, equipmentLaborSavingFacilityCurrency),
      costAvoided: pick(costAvoidedCustomerCurrency, costAvoidedFacilityCurrency),
    };
  };

export const useCostSavingsDetailsQuery = (keys: ICostSavingDetailsKeys) => {
  return useQuery({
    queryKey: totalCostSavingsKey(keys),
    queryFn: totalCostSavingsFn,
    select: totalCostSavingsSelect(keys.locationIds),
  });
};
